<template>
  <div class="py-6">
    <form @submit.prevent ref="form" class="space-y-6">
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-800">
          Informations de l'accompagnement
        </h2>

        <!-- Nom et Prix -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Nom
            </label>
            <input
              required
              type="text"
              v-model="accompaniement.name"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs"
              placeholder="Ex: Frites de pommes de terre"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Prix (FCFA)
            </label>
            <input
              required
              type="number"
              min="0"
              v-model.number="accompaniement.price"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs"
              placeholder="Ex: 500"
            />
          </div>
        </div>

        <!-- Description -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            required
            v-model="accompaniement.description"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs"
            placeholder="Ex: Frites de pommes de terre"
          ></textarea>
        </div>

        <!-- Image -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Image
          </label>
          <FileUploader
            placeHolder="Choisir une image pour l'accompagnement"
            :acceptedFilesTypes="['image/*']"
            @onFileChangedV2="handleImageUpload"
            :fileId="accompaniement.image"
          />
          <div v-if="accompaniement.image" class="mt-3">
            <img
              :src="accompaniement.image"
              alt="Preview"
              class="h-20 w-20 object-contain rounded-lg border"
            />
          </div>
        </div>
      </div>

      <button class="hidden"></button>
    </form>

    <!-- Bouton d'enregistrement -->
    <div class="flex justify-end !mt-12">
      <button type="button" @click="startSave" class="buttonComponent w-full">
        Enregistrer l'accompagnement
      </button>
    </div>

    <!-- ActionTrigger -->
    <ActionTrigger
      dataKey="saveAccompaniement"
      ref="saveAccompaniement"
      @success="handleSaveSuccess"
      @failed="
        err => {
          Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
            title: 'Échec d\'enregistrement',
            body: err.result?.message || 'Erreur inconnue',
          })
        }
      "
    />
  </div>
</template>

<script setup>
import { reactive, ref, watch, toRaw } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import FileUploader from '@/presentation/components/input/FileUploader.vue'

const props = defineProps({
  accompaniementData: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['saved'])

const saveAccompaniement = ref(null)
const form = ref(null)
/** @type {import('vue').Ref<{ id: string | null, name: string, description: string, price: number, image: string } | null>} */
const pendingCreateData = ref(null) // pour stocker les données à créer après un delete

const accompaniement = reactive({
  id: null,
  name: '',
  description: '',
  price: 0,
  image: '',
})

watch(
  () => props.accompaniementData,
  val => {
    if (val && Object.keys(val).length > 0) {
      Object.assign(accompaniement, val)
    } else {
      // reset pour une création
      accompaniement.id = null
      accompaniement.name = ''
      accompaniement.description = ''
      accompaniement.price = 0
      accompaniement.image = ''
    }
  },
  { immediate: true },
)

function startSave() {
  const isValid = form.value.checkValidity()
  if (!isValid) {
    form.value.reportValidity()
    return
  }
  saveItem()
}

function handleImageUpload(fileData) {
  accompaniement.image = fileData.url
}

function handleSaveSuccess(res) {
  // si on a des données en attente de création, c'est qu'on vient de faire un delete
  if (pendingCreateData.value) {
    // maintenant on fait le create avec les mêmes données
    const dataToCreate = pendingCreateData.value
    pendingCreateData.value = null

    saveAccompaniement.value?.trigger({
      action: 'create',
      id: dataToCreate.id,
      name: dataToCreate.name,
      description: dataToCreate.description,
      price: dataToCreate.price,
      image: dataToCreate.image,
    })
  } else {
    if (res.result?.data?.id && !res.result?.data?.isActive) {
      saveAccompaniement.value?.trigger({
        action: 'activate',
        id: res.result?.data?.id,
      })
    }
    emit('saved')
  }
}

function saveItem() {
  // utiliser toRaw pour obtenir les valeurs actuelles sans la réactivité
  const rawData = toRaw(accompaniement)
  console.log('🚀 ~ saveItem ~ accompaniement:', rawData)

  const accompaniementData = {
    id: rawData.id,
    name: rawData.name,
    description: rawData.description,
    price: rawData.price,
    image: rawData.image,
  }

  console.log('🚀 ~ saveItem ~ payload to send:', accompaniementData)

  // si c'est une modification, on fait delete puis create
  if (rawData.id) {
    // stocker les données pour le create futur
    // pendingCreateData.value = accompaniementData

    // d'abord supprimer l'ancien élément
    // saveAccompaniement.value?.trigger({
    //   action: 'delete',
    //   id: rawData.id,
    // })
    saveAccompaniement.value?.trigger({
      action: 'update',
      ...accompaniementData,
    })
  } else {
    // création simple
    saveAccompaniement.value?.trigger({
      action: 'create',
      ...accompaniementData,
    })
  }
}
</script>

<style scoped>
img {
  transition: transform 0.2s ease-in-out;
}
img:hover {
  transform: scale(1.05);
}
</style>
